// CoursesDetails.js - Courses Details section component
import { DEVICES } from '../../../constants/breakpoints';

export const createCoursesDetailsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const detailsSectionClass = 'courses-details-section';
  const detailsContainerClass = 'courses-details-container';
  const detailsHeaderClass = 'courses-details-header';
  const detailsTitleClass = 'courses-details-title';
  const detailsSubtitleClass = 'courses-details-subtitle';
  const detailsGridClass = 'courses-details-grid';
  const courseCardClass = 'courses-course-card';
  const courseImageClass = 'courses-course-image';
  const courseContentClass = 'courses-course-content';
  const courseIconClass = 'courses-course-icon';
  const courseNameClass = 'courses-course-name';
  const courseDescClass = 'courses-course-description';
  const courseStatsClass = 'courses-course-stats';
  const courseStatClass = 'courses-course-stat';
  const courseStatNumberClass = 'courses-course-stat-number';
  const courseStatLabelClass = 'courses-course-stat-label';
  const courseButtonClass = 'courses-course-button';
  const coursePriceClass = 'courses-course-price';
  const courseTagsClass = 'courses-course-tags';
  const courseTagClass = 'courses-course-tag';
  const courseBadgeClass = 'courses-course-badge';
  const detailsPatternOverlayClass = 'courses-details-pattern-overlay';

  // Desktop Styles - Modern courses theme with enhanced design
  const detailsSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.08) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.06) 0%, transparent 60%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)
    `,
    color: '#1e293b',
    width: '100%',
    overflow: 'hidden'
  };

  const detailsPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      linear-gradient(45deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px),
      linear-gradient(-45deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px',
    zIndex: '0',
    opacity: '0.4'
  };

  const detailsContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const detailsHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px',
    position: 'relative'
  };

  const detailsTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.02em',
    lineHeight: '1.1',
    position: 'relative'
  };

  const detailsSubtitleStyles = {
    fontSize: '18px',
    color: '#64748b',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const detailsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '32px',
    marginTop: '60px'
  };

  const courseCardStyles = {
    background: '#ffffff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    position: 'relative',
    height: 'auto',
    minHeight: '580px',
    display: 'flex',
    flexDirection: 'column'
  };

  const courseCardHoverStyles = {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 30px rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.2)'
  };

  const courseImageStyles = {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    transition: 'all 0.4s ease',
    filter: 'brightness(0.95) saturate(1.1)',
    flexShrink: 0
  };

  const courseImageHoverStyles = {
    filter: 'brightness(1.05) saturate(1.2)',
    transform: 'scale(1.05)'
  };

  const courseContentStyles = {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    position: 'relative'
  };

  const courseBadgeStyles = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif',
    zIndex: '2'
  };

  const courseIconStyles = {
    fontSize: '32px',
    color: '#6366f1',
    marginBottom: '16px',
    display: 'block',
    transition: 'all 0.3s ease',
    textShadow: '0 2px 8px rgba(99, 102, 241, 0.2)'
  };

  const courseIconHoverStyles = {
    fontSize: '36px',
    textShadow: '0 4px 16px rgba(99, 102, 241, 0.4)',
    transform: 'scale(1.1) rotate(5deg)'
  };

  const courseNameStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.2'
  };

  const courseDescStyles = {
    fontSize: '14px',
    color: '#64748b',
    lineHeight: '1.6',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    flex: '1'
  };

  const courseStatsStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    gap: '16px',
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.03) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(99, 102, 241, 0.1)'
  };

  const courseStatStyles = {
    textAlign: 'center',
    flex: '1'
  };

  const courseStatNumberStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#6366f1',
    display: 'block',
    fontFamily: '"Inter", sans-serif',
    marginBottom: '4px'
  };

  const courseStatLabelStyles = {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '500'
  };

  const courseTagsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px'
  };

  const courseTagStyles = {
    padding: '4px 8px',
    background: 'rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#6366f1',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease'
  };

  const courseTagHoverStyles = {
    background: 'rgba(99, 102, 241, 0.2)',
    border: '1px solid rgba(99, 102, 241, 0.4)',
    color: '#4f46e5',
    transform: 'translateY(-2px)'
  };

  const coursePriceStyles = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#059669',
    marginBottom: '16px',
    fontFamily: '"Inter", sans-serif',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const courseButtonStyles = {
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    position: 'relative',
    overflow: 'hidden',
    marginTop: 'auto'
  };

  const courseButtonHoverStyles = {
    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)'
  };

  // Tablet Styles
  const tabletDetailsSectionStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const tabletDetailsTitleStyles = {
    fontSize: '40px'
  };

  const tabletDetailsSubtitleStyles = {
    fontSize: '16px'
  };

  const tabletDetailsGridStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '28px'
  };

  const tabletCourseCardStyles = {
    height: 'auto',
    minHeight: '540px'
  };

  const tabletCourseContentStyles = {
    padding: '20px'
  };

  const tabletCourseIconStyles = {
    fontSize: '28px'
  };

  const tabletCourseNameStyles = {
    fontSize: '22px'
  };

  const tabletCoursePriceStyles = {
    fontSize: '22px'
  };

  // Mobile Styles
  const mobileDetailsSectionStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const mobileDetailsTitleStyles = {
    fontSize: '32px'
  };

  const mobileDetailsSubtitleStyles = {
    fontSize: '16px'
  };

  const mobileDetailsGridStyles = {
    gridTemplateColumns: '1fr',
    gap: '24px'
  };

  const mobileCourseCardStyles = {
    height: 'auto',
    minHeight: '500px'
  };

  const mobileCourseContentStyles = {
    padding: '20px'
  };

  const mobileCourseIconStyles = {
    fontSize: '28px'
  };

  const mobileCourseNameStyles = {
    fontSize: '20px'
  };

  const mobileCourseStatsStyles = {
    flexDirection: 'column',
    gap: '12px'
  };

  const mobileCoursePriceStyles = {
    fontSize: '20px'
  };

  const mobileCourseButtonStyles = {
    padding: '12px 20px'
  };

  // Update registry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(detailsSectionClass, detailsSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(detailsPatternOverlayClass, detailsPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(detailsContainerClass, detailsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(detailsHeaderClass, detailsHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(detailsTitleClass, detailsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(detailsSubtitleClass, detailsSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(detailsGridClass, detailsGridStyles);
    newRegistry[DEVICES.DESKTOP].set(courseCardClass, courseCardStyles);
    newRegistry[DEVICES.DESKTOP].set(courseImageClass, courseImageStyles);
    newRegistry[DEVICES.DESKTOP].set(courseContentClass, courseContentStyles);
    newRegistry[DEVICES.DESKTOP].set(courseBadgeClass, courseBadgeStyles);
    newRegistry[DEVICES.DESKTOP].set(courseIconClass, courseIconStyles);
    newRegistry[DEVICES.DESKTOP].set(courseNameClass, courseNameStyles);
    newRegistry[DEVICES.DESKTOP].set(courseDescClass, courseDescStyles);
    newRegistry[DEVICES.DESKTOP].set(courseStatsClass, courseStatsStyles);
    newRegistry[DEVICES.DESKTOP].set(courseStatClass, courseStatStyles);
    newRegistry[DEVICES.DESKTOP].set(courseStatNumberClass, courseStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(courseStatLabelClass, courseStatLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(courseTagsClass, courseTagsStyles);
    newRegistry[DEVICES.DESKTOP].set(courseTagClass, courseTagStyles);
    newRegistry[DEVICES.DESKTOP].set(coursePriceClass, coursePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(courseButtonClass, courseButtonStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${courseCardClass}--hover`, courseCardHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${courseImageClass}--hover`, courseImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${courseIconClass}--hover`, courseIconHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${courseTagClass}--hover`, courseTagHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${courseButtonClass}--hover`, courseButtonHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(detailsSectionClass, tabletDetailsSectionStyles);
    newRegistry[DEVICES.TABLET].set(detailsTitleClass, tabletDetailsTitleStyles);
    newRegistry[DEVICES.TABLET].set(detailsSubtitleClass, tabletDetailsSubtitleStyles);
    newRegistry[DEVICES.TABLET].set(detailsGridClass, tabletDetailsGridStyles);
    newRegistry[DEVICES.TABLET].set(courseCardClass, tabletCourseCardStyles);
    newRegistry[DEVICES.TABLET].set(courseContentClass, tabletCourseContentStyles);
    newRegistry[DEVICES.TABLET].set(courseIconClass, tabletCourseIconStyles);
    newRegistry[DEVICES.TABLET].set(courseNameClass, tabletCourseNameStyles);
    newRegistry[DEVICES.TABLET].set(coursePriceClass, tabletCoursePriceStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(detailsSectionClass, mobileDetailsSectionStyles);
    newRegistry[DEVICES.MOBILE].set(detailsTitleClass, mobileDetailsTitleStyles);
    newRegistry[DEVICES.MOBILE].set(detailsSubtitleClass, mobileDetailsSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(detailsGridClass, mobileDetailsGridStyles);
    newRegistry[DEVICES.MOBILE].set(courseCardClass, mobileCourseCardStyles);
    newRegistry[DEVICES.MOBILE].set(courseContentClass, mobileCourseContentStyles);
    newRegistry[DEVICES.MOBILE].set(courseIconClass, mobileCourseIconStyles);
    newRegistry[DEVICES.MOBILE].set(courseNameClass, mobileCourseNameStyles);
    newRegistry[DEVICES.MOBILE].set(courseStatsClass, mobileCourseStatsStyles);
    newRegistry[DEVICES.MOBILE].set(coursePriceClass, mobileCoursePriceStyles);
    newRegistry[DEVICES.MOBILE].set(courseButtonClass, mobileCourseButtonStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    detailsSectionClass, detailsContainerClass, detailsHeaderClass,
    detailsTitleClass, detailsSubtitleClass, detailsGridClass,
    courseCardClass, courseImageClass, courseContentClass,
    courseBadgeClass, courseIconClass, courseNameClass, courseDescClass,
    courseStatsClass, courseStatClass, courseStatNumberClass,
    courseStatLabelClass, courseTagsClass, courseTagClass,
    coursePriceClass, courseButtonClass, detailsPatternOverlayClass
  ]));

  // Generate IDs
  const baseId = Date.now();
  const sectionId = baseId;
  const patternOverlayId = baseId + 1;
  const containerId = baseId + 2;
  const headerId = baseId + 3;
  const titleId = baseId + 4;
  const subtitleId = baseId + 5;
  const gridId = baseId + 6;

  // Enhanced course cards data with tags and badges
  const courses = [
    {
      id: 1000,
      icon: '💻',
      name: 'Computer Operator',
      description: 'Master essential computer skills including MS Office, data entry, file management, and basic troubleshooting. Perfect for office work and administrative roles.',
      image: 'https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?w=400&h=200&fit=crop',
      students: '2.5K+',
      lessons: '25',
      duration: '6 weeks',
      price: '$79',
      originalPrice: '$120',
      badge: 'Popular',
      tags: ['MS Office', 'Data Entry', 'Admin'],
      linkTo: 'computeroperator'
    },
    {
      id: 2000,
      icon: '🧵',
      name: 'Textile Designing',
      description: 'Learn fabric design, pattern making, color theory, and textile production techniques. Create beautiful designs for fashion and home textiles.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=200&fit=crop',
      students: '1.8K+',
      lessons: '32',
      duration: '8 weeks',
      price: '$99',
      originalPrice: '$150',
      badge: 'Creative',
      tags: ['Design', 'Fashion', 'Patterns'],
      linkTo: 'textiledesigning'
    },
    {
      id: 3000,
      icon: '📋',
      name: 'Import Export Procedure and Documents',
      description: 'Comprehensive training on international trade procedures, customs documentation, shipping processes, and export-import regulations.',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=200&fit=crop',
      students: '3.2K+',
      lessons: '28',
      duration: '7 weeks',
      price: '$129',
      originalPrice: '$180',
      badge: 'Professional',
      tags: ['Trade', 'Documentation', 'Business'],
      linkTo: 'importexportprocedure'
    },
    {
      id: 4000,
      icon: '📱',
      name: 'Digital Marketing',
      description: 'Master social media marketing, SEO, content creation, Google Ads, and analytics. Build successful online marketing campaigns.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop',
      students: '4.5K+',
      lessons: '35',
      duration: '10 weeks',
      price: '$149',
      originalPrice: '$220',
      badge: 'Bestseller',
      tags: ['SEO', 'Social Media', 'Analytics'],
      linkTo: 'digitalmarketing'
    },
    {
      id: 5000,
      icon: '✂️',
      name: 'Dress Making',
      description: 'Learn professional tailoring, pattern cutting, sewing techniques, and garment construction. Create custom clothing from scratch.',
      image: 'https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=400&h=200&fit=crop',
      students: '2.1K+',
      lessons: '30',
      duration: '9 weeks',
      price: '$89',
      originalPrice: '$140',
      badge: 'Hands-on',
      tags: ['Tailoring', 'Sewing', 'Fashion'],
      linkTo: 'dressmaking'
    },
    {
      id: 6000,
      icon: '💄',
      name: 'Beautician',
      description: 'Professional beauty training including skincare, makeup artistry, hair styling, nail art, and salon management techniques.',
      image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=200&fit=crop',
      students: '3.8K+',
      lessons: '40',
      duration: '12 weeks',
      price: '$159',
      originalPrice: '$240',
      badge: 'Comprehensive',
      tags: ['Makeup', 'Skincare', 'Hair'],
      linkTo: 'beautician'
    },
    {
      id: 7000,
      icon: '⚡',
      name: 'Electrician',
      description: 'Complete electrical training covering wiring, circuit installation, safety protocols, electrical troubleshooting, and maintenance.',
      image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=200&fit=crop',
      students: '2.9K+',
      lessons: '45',
      duration: '14 weeks',
      price: '$199',
      originalPrice: '$280',
      badge: 'Technical',
      tags: ['Wiring', 'Safety', 'Maintenance'],
      linkTo: 'electrician'
    }
  ];

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses Details Section',
      parentId: null,
      className: detailsSectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'courses-details-section',
        'data-page-id': currentPageId
      }
    },

    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pattern Overlay',
      parentId: sectionId,
      hierarchyName: "Pattern Overlay",
      className: detailsPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-details-pattern-overlay'
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Details Container',
      parentId: sectionId,
      className: detailsContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: ''
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Details Header',
      parentId: containerId,
      className: detailsHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: ''
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Details Title',
      parentId: headerId,
      className: detailsTitleClass,
      styles: {},
      children: [],
      text: 'Professional Training Courses'
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Details Subtitle',
      parentId: headerId,
      className: detailsSubtitleClass,
      styles: {},
      children: [],
      text: 'Develop practical skills with our comprehensive vocational training programs designed to prepare you for real-world career opportunities.'
    },
    
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Courses Grid',
      parentId: containerId,
      className: detailsGridClass,
      styles: {},
      children: courses.map(course => course.id),
      text: ''
    }
  ];

  // Add enhanced course cards
  courses.forEach((course, index) => {
    const cardId = course.id;
    const badgeId = cardId + 1;
    const imageId = cardId + 2;
    const contentId = cardId + 3;
    const iconId = cardId + 4;
    const nameId = cardId + 5;
    const descId = cardId + 6;
    const tagsId = cardId + 7;
    const statsId = cardId + 8;
    const stat1Id = cardId + 9;
    const stat1NumId = cardId + 10;
    const stat1LabelId = cardId + 11;
    const stat2Id = cardId + 12;
    const stat2NumId = cardId + 13;
    const stat2LabelId = cardId + 14;
    const stat3Id = cardId + 15;
    const stat3NumId = cardId + 16;
    const stat3LabelId = cardId + 17;
    const priceId = cardId + 18;
    const buttonId = cardId + 19;

    newElements.push(
      // Course card
      {
        id: cardId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Course Card ${index + 1}`,
        parentId: gridId,
        className: courseCardClass,
        styles: {},
        children: [badgeId, imageId, contentId],
        text: ''
      },

      // Course badge
      {
        id: badgeId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Course Badge ${index + 1}`,
        parentId: cardId,
        className: courseBadgeClass,
        styles: {},
        children: [],
        text: course.badge
      },
      
      // Course image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Course Image ${index + 1}`,
        parentId: cardId,
        className: courseImageClass,
        styles: {},
        children: [],
        text: '',
        src: course.image,
        attributes: { alt: course.name }
      },
      
      // Course content
      {
        id: contentId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Course Content ${index + 1}`,
        parentId: cardId,
        className: courseContentClass,
        styles: {},
        children: [iconId, nameId, descId, tagsId, statsId, priceId, buttonId],
        text: ''
      },
      
      // Course icon
      {
        id: iconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Course Icon ${index + 1}`,
        parentId: contentId,
        className: courseIconClass,
        styles: {},
        children: [],
        text: course.icon
      },
      
      // Course name
      {
        id: nameId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h3',
        name: `Course Name ${index + 1}`,
        parentId: contentId,
        className: courseNameClass,
        styles: {},
        children: [],
        text: course.name
      },
      
      // Course description
      {
        id: descId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: `Course Description ${index + 1}`,
        parentId: contentId,
        className: courseDescClass,
        styles: {},
        children: [],
        text: course.description
      },

      // Course tags
      {
        id: tagsId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Course Tags ${index + 1}`,
        parentId: contentId,
        className: courseTagsClass,
        styles: {},
        children: course.tags.map((tag, tagIndex) => tagsId + 100 + tagIndex),
        text: ''
      },
      
      // Course stats
      {
        id: statsId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Course Stats ${index + 1}`,
        parentId: contentId,
        className: courseStatsClass,
        styles: {},
        children: [stat1Id, stat2Id, stat3Id],
        text: ''
      },
      
      // Stat 1 (Students)
      {
        id: stat1Id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Stat 1 ${index + 1}`,
        parentId: statsId,
        className: courseStatClass,
        styles: {},
        children: [stat1NumId, stat1LabelId],
        text: ''
      },
      {
        id: stat1NumId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Stat 1 Number ${index + 1}`,
        parentId: stat1Id,
        className: courseStatNumberClass,
        styles: {},
        children: [],
        text: course.students
      },
      {
        id: stat1LabelId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Stat 1 Label ${index + 1}`,
        parentId: stat1Id,
        className: courseStatLabelClass,
        styles: {},
        children: [],
        text: 'Students'
      },
      
      // Stat 2 (Lessons)
      {
        id: stat2Id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Stat 2 ${index + 1}`,
        parentId: statsId,
        className: courseStatClass,
        styles: {},
        children: [stat2NumId, stat2LabelId],
        text: ''
      },
      {
        id: stat2NumId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Stat 2 Number ${index + 1}`,
        parentId: stat2Id,
        className: courseStatNumberClass,
        styles: {},
        children: [],
        text: course.lessons
      },
      {
        id: stat2LabelId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Stat 2 Label ${index + 1}`,
        parentId: stat2Id,
        className: courseStatLabelClass,
        styles: {},
        children: [],
        text: 'Lessons'
      },
      
      // Stat 3 (Duration)
      {
        id: stat3Id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Stat 3 ${index + 1}`,
        parentId: statsId,
        className: courseStatClass,
        styles: {},
        children: [stat3NumId, stat3LabelId],
        text: ''
      },
      {
        id: stat3NumId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Stat 3 Number ${index + 1}`,
        parentId: stat3Id,
        className: courseStatNumberClass,
        styles: {},
        children: [],
        text: course.duration
      },
      {
        id: stat3LabelId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Stat 3 Label ${index + 1}`,
        parentId: stat3Id,
        className: courseStatLabelClass,
        styles: {},
        children: [],
        text: 'Duration'
      },
      
      // // Course price
      // {
      //   id: priceId,
      //   builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      //   type: 'div',
      //   name: `Course Price ${index + 1}`,
      //   parentId: contentId,
      //   className: coursePriceClass,
      //   styles: {},
      //   children: [],
      //   text: `${course.price} ${course.originalPrice ? `(was ${course.originalPrice})` : ''}`
      // },
      
      // Course detail button
      {
        id: buttonId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'button',
        name: `Course Detail Button ${index + 1}`,
        parentId: contentId,
        className: courseButtonClass,
        styles: {},
        children: [],
        text: 'Details',
        stateClassName: {
          hover: '',
          pressed: '',
          focused: ''
        },
        currentState: 'default',
        linkTo: course.linkTo
      }
    );

    // Add individual tag elements
    course.tags.forEach((tag, tagIndex) => {
      newElements.push({
        id: tagsId + 100 + tagIndex,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Tag ${tagIndex + 1} - ${tag}`,
        parentId: tagsId,
        className: courseTagClass,
        styles: {},
        children: [],
        text: tag
      });
    });
  });

  return {
    detailsSection: newElements[0],
    elements: newElements
  };
}; 